/**
 * Config ngưỡng thu (US-E16.F6-0187 · @UC9249) — @gate money,config.
 *
 * THUẦN: không DB, không I/O, deterministic. 3 khoá "cửa ngõ" của luồng thu:
 *  - `collection_grace_days` — số NGÀY ân hạn sau `dueDate` trước khi due bị đánh `overdue`.
 *    Là SỐ NGÀY (không phải tiền) ⇒ số nguyên JS, biên [0, 90]. KHÔNG BigInt/Decimal.
 *  - `allow_partial_pay` — bật/tắt đóng MỘT PHẦN (F4 · @UC9241). false ⇒ record-paid partial 422.
 *  - `overpay_credit_policy` — xử lý phần đóng DƯ (F4 · @UC9242): `credit` (→ advance_credit — mặc
 *    định, giữ hành vi F4), `refund` (đánh dấu chờ hoàn — KHÔNG thành số dư ứng trước), `reject` (422).
 *
 * DEFAULT = giữ NGUYÊN hành vi F4 (grace 0, partial bật, overpay→credit) ⇒ bật config KHÔNG regress.
 * Fail-closed: key lạ ⇒ throw (mẫu `validateMoneyPolicyValue`).
 */
import { DomainError } from "./errors.js";

/** 3 khoá config ngưỡng thu (registry `system_settings`, override được ở fund/group). */
export const COLLECTION_CONFIG_KEYS = [
  "collection_grace_days",
  "allow_partial_pay",
  "overpay_credit_policy",
] as const;

export type CollectionConfigKey = (typeof COLLECTION_CONFIG_KEYS)[number];

/** Chính sách xử lý phần đóng DƯ (overpay) — F4 mặc định `credit`. */
export const OVERPAY_CREDIT_POLICIES = ["credit", "refund", "reject"] as const;
export type OverpayCreditPolicy = (typeof OVERPAY_CREDIT_POLICIES)[number];

/** Biên số ngày ân hạn (inclusive). Ngày — KHÔNG phải tiền. */
export const COLLECTION_GRACE_DAYS_MIN = 0;
export const COLLECTION_GRACE_DAYS_MAX = 90;

/**
 * Default hệ thống — CHỌN ĐỂ GIỮ hành vi F4 (không regress khi seed config lần đầu):
 * grace 0 (overdue ngay sau ngày đến hạn như 0059), partial BẬT, overpay → advance_credit.
 */
export const COLLECTION_CONFIG_DEFAULTS: {
  readonly collection_grace_days: number;
  readonly allow_partial_pay: boolean;
  readonly overpay_credit_policy: OverpayCreditPolicy;
} = {
  collection_grace_days: 0,
  allow_partial_pay: true,
  overpay_credit_policy: "credit",
};

/** True nếu `key` là khoá config thu (type-guard). */
export function isCollectionConfigKey(key: string): key is CollectionConfigKey {
  return (COLLECTION_CONFIG_KEYS as readonly string[]).includes(key);
}

/** True nếu `value` là chính sách overpay hợp lệ (type-guard cho resolver). */
export function isOverpayCreditPolicy(value: unknown): value is OverpayCreditPolicy {
  return (
    typeof value === "string" &&
    (OVERPAY_CREDIT_POLICIES as readonly string[]).includes(value)
  );
}

/** True nếu `value` là số ngày ân hạn hợp biên (int ∈ [0,90]) — dùng cho resolver fail-safe. */
export function isCollectionGraceDays(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= COLLECTION_GRACE_DAYS_MIN &&
    value <= COLLECTION_GRACE_DAYS_MAX
  );
}

/**
 * Validate giá trị MỚI của một khoá config thu TRƯỚC khi ghi (registry edit / fund override).
 * Ném `DomainError(code)` khi vi phạm; void khi hợp lệ. Pure/deterministic — fail-closed key lạ.
 */
export function validateCollectionConfig(key: CollectionConfigKey, value: unknown): void {
  switch (key) {
    case "collection_grace_days": {
      if (!isCollectionGraceDays(value)) {
        throw new DomainError("COLLECTION_CONFIG_OUT_OF_RANGE");
      }
      return;
    }

    case "allow_partial_pay": {
      if (typeof value !== "boolean") {
        throw new DomainError("COLLECTION_CONFIG_INVALID");
      }
      return;
    }

    case "overpay_credit_policy": {
      if (!isOverpayCreditPolicy(value)) {
        throw new DomainError("COLLECTION_CONFIG_INVALID");
      }
      return;
    }

    default: {
      // Khoá không thuộc config thu không nên tới đây — fail-closed.
      throw new DomainError("COLLECTION_CONFIG_INVALID");
    }
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Đầu ngày UTC (00:00) của `d` — `Collection.dueDate` là @db.Date (00:00 UTC). */
function startOfDayUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * CONSUMER `collection_grace_days` (hàm THUẦN): due đã QUÁ HẠN chưa tại thời điểm `now`?
 *
 * Quá hạn ⟺ `now ≥ startOfDay(dueDate) + (grace + 1) ngày`:
 *  - Trọn NGÀY đến hạn KHÔNG overdue (0059 hiện hành).
 *  - `grace = 0` ⇒ overdue từ 00:00 ngày SAU dueDate (KHÔNG regress hành vi cũ).
 *  - `grace = N` ⇒ còn N ngày ân hạn sau ngày đến hạn mới overdue.
 * `graceDays` bẩn/âm ⇒ coi như 0 (fail-safe: KHÔNG kéo dài ân hạn vô hạn do config rác).
 */
export function isDueOverdue(dueDate: Date, now: Date, graceDays: number): boolean {
  const grace = isCollectionGraceDays(graceDays) ? graceDays : COLLECTION_GRACE_DAYS_MIN;
  const overdueFrom = startOfDayUtc(dueDate).getTime() + (grace + 1) * DAY_MS;
  return now.getTime() >= overdueFrom;
}
